import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useDataRefresh, DEFAULT_POLL_INTERVAL_MS } from '@/hooks/useDataRefresh';
import { Plus, Search, Users, UserCheck, UserX, Mail, IdCard, Briefcase, Clock, Timer } from 'lucide-react';
import { TimeClockWidget, TimeTrackingPanel } from '@/components/TimeTracking';

interface Profile {
  id: string;
  full_name: string;
  tag: string | null;
  employee_id: string | null;
  area: string | null;
  shift: string | null;
  is_active: boolean;
  created_at: string;
  user_roles: Array<{
    role: 'operator' | 'supervisor' | 'admin';
  }>;
}

export default function Personal() {
  const { userRole } = useAuth();
  const { toast } = useToast();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [filterArea, setFilterArea] = useState<string>('all');

  const fetchProfiles = async () => {
    try {
      const { data: profilesData, error: profilesError } = await api
        .from('profiles')
        .select('*')
        .order('full_name', { ascending: true });

      if (profilesError) throw profilesError;

      // Fetch roles for each profile
      const { data: rolesData, error: rolesError } = await api
        .from('user_roles')
        .select('user_id, role');

      if (rolesError) throw rolesError;

      // Combine data
      const combinedData = profilesData?.map(profile => ({
        ...profile,
        user_roles: rolesData?.filter(r => r.user_id === profile.id).map(r => ({ role: r.role })) || []
      }));

      setProfiles(combinedData || []);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los perfiles',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfiles();
  }, []);
  useDataRefresh(fetchProfiles, { pollIntervalMs: DEFAULT_POLL_INTERVAL_MS });

  const filteredProfiles = profiles.filter(profile => {
    const matchesSearch =
      profile.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      profile.employee_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      profile.tag?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'active' && profile.is_active) ||
      (filterStatus === 'inactive' && !profile.is_active);

    const matchesArea = filterArea === 'all' || profile.area === filterArea;

    return matchesSearch && matchesStatus && matchesArea;
  });

  // Get unique areas
  const areas = Array.from(new Set(profiles.map(p => p.area).filter(Boolean))) as string[];

  const canManage = userRole === 'admin' || userRole === 'supervisor';

  const getRoleBadge = (roles: Profile['user_roles']) => {
    if (roles.length === 0) return <Badge variant="secondary">Sin rol</Badge>;

    const role = roles[0].role;
    const variants = {
      admin: 'destructive' as const,
      supervisor: 'default' as const,
      operator: 'secondary' as const,
    };

    const labels = {
      admin: 'Administrador',
      supervisor: 'Supervisor',
      operator: 'Operador',
    };

    return <Badge variant={variants[role]}>{labels[role]}</Badge>;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
              Personal
            </h1>
            <p className="text-muted-foreground mt-2">
              Gestión de personal, fichajes y control horario
            </p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="fichaje" className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="fichaje" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Mi Fichaje
            </TabsTrigger>
            <TabsTrigger value="control" className="flex items-center gap-2">
              <Timer className="w-4 h-4" />
              Control
            </TabsTrigger>
            <TabsTrigger value="directorio" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Directorio
            </TabsTrigger>
          </TabsList>

          {/* Tab: Mi Fichaje */}
          <TabsContent value="fichaje" className="space-y-6">
            <div className="max-w-lg mx-auto">
              <TimeClockWidget />
            </div>
          </TabsContent>

          {/* Tab: Control (Supervisores/Admin) */}
          <TabsContent value="control" className="space-y-6">
            {canManage ? (
              <TimeTrackingPanel embedded />
            ) : (
              <Card className="glass-card p-12 text-center">
                <Users className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg text-muted-foreground">
                  No tienes permisos para ver el control de fichajes
                </p>
              </Card>
            )}
          </TabsContent>

          {/* Tab: Directorio */}
          <TabsContent value="directorio" className="space-y-6">

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre, ID o tag..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="flex gap-2">
            <Button
              variant={filterStatus === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus('all')}
            >
              Todos
            </Button>
            <Button
              variant={filterStatus === 'active' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus('active')}
            >
              Activos
            </Button>
            <Button
              variant={filterStatus === 'inactive' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus('inactive')}
            >
              Inactivos
            </Button>
          </div>

          {areas.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={filterArea === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterArea('all')}
              >
                Todas las áreas
              </Button>
              {areas.map(area => (
                <Button
                  key={area}
                  variant={filterArea === area ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterArea(area)}
                >
                  {area}
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Personal</p>
                <p className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
                  {profiles.length}
                </p>
              </div>
              <Users className="w-8 h-8 text-primary/50" />
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Activos</p>
                <p className="text-3xl font-bold text-success">
                  {profiles.filter(p => p.is_active).length}
                </p>
              </div>
              <UserCheck className="w-8 h-8 text-success/50" />
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Inactivos</p>
                <p className="text-3xl font-bold text-muted-foreground">
                  {profiles.filter(p => !p.is_active).length}
                </p>
              </div>
              <UserX className="w-8 h-8 text-muted-foreground/50" />
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Áreas</p>
                <p className="text-3xl font-bold text-primary">
                  {areas.length}
                </p>
              </div>
              <Briefcase className="w-8 h-8 text-primary/50" />
            </div>
          </Card>
        </div>

        {/* Profiles Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : filteredProfiles.length === 0 ? (
          <Card className="glass-card p-12 text-center">
            <Users className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg text-muted-foreground">
              {searchTerm ? 'No se encontraron perfiles' : 'No hay personal registrado'}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProfiles.map((profile) => (
              <Card key={profile.id} className="glass-card p-6 hover:shadow-magma transition-all">
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-magma flex items-center justify-center text-white font-bold text-lg">
                        {profile.full_name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{profile.full_name}</h3>
                        {getRoleBadge(profile.user_roles)}
                      </div>
                    </div>
                    <Badge variant={profile.is_active ? 'default' : 'secondary'}>
                      {profile.is_active ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </div>

                  <div className="space-y-2 text-sm">
                    {profile.employee_id && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <IdCard className="w-4 h-4" />
                        <span>ID: {profile.employee_id}</span>
                      </div>
                    )}

                    {profile.tag && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <span className="font-mono bg-muted px-2 py-1 rounded text-xs">
                          {profile.tag}
                        </span>
                      </div>
                    )}

                    {profile.area && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Briefcase className="w-4 h-4" />
                        <span>Área: {profile.area}</span>
                      </div>
                    )}

                    {profile.shift && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <span>Turno: {profile.shift}</span>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
